import type { LucideIcon } from 'lucide-react'
import { cn } from './cn'

/**
 * Enveloppe de pictogramme.
 *
 * Les maquettes dessinent leurs icônes en SVG inline au style Lucide — même
 * viewBox 24, même trait 1,5, mêmes extrémités arrondies. On utilise donc
 * directement Lucide plutôt que de recopier une table de tracés qu'il faudrait
 * ensuite maintenir.
 *
 * Ce composant existe pour une seule raison : garantir le trait de 1,5px imposé
 * par la charte. Lucide dessine à 2 par défaut, et un `strokeWidth` oublié se
 * voit immédiatement à côté des autres.
 */
export function Icon({
  as: Glyph,
  size = 18,
  trait = 1.5,
  className,
}: {
  as: LucideIcon
  /**
   * 18 dans les menus et les lignes de tableau, 20 dans les en-têtes, 16 en
   * badge — et 19 dans le rail Oasis, qui est la mesure de la maquette.
   */
  size?: 14 | 16 | 18 | 19 | 20 | 24
  /**
   * Épaisseur du tracé.
   *
   * ⚠️ **1,5 reste le défaut, et il n'a pas bougé** : c'est la valeur de la
   * charte, et tout appel qui ne dit rien la reçoit — les cinq espaces
   * continuent donc de rendre exactement ce qu'ils rendaient.
   *
   * La maquette Oasis dessine plus épais là où le glyphe porte seul le sens :
   * 1,6 dans le rail d'icônes, qui n'affiche aucun libellé, 1,7 sur les
   * pastilles du menu mobile, 1,9 sur le bouton de menu. Arbitrage de Baptiste
   * le 2026-08-24 : « reprends fidèlement la maquette même si la charte
   * graphique est un peu différente ». On ouvre donc la valeur plutôt que de
   * corriger la maquette au nom de la charte.
   */
  trait?: number
  className?: string
}) {
  return (
    <Glyph
      size={size}
      strokeWidth={trait}
      aria-hidden="true"
      className={cn('shrink-0', className)}
    />
  )
}
